import type { RunnitNodeRunResult, RunnitUpload } from '@run-diffusion/shared';

/** Stable identity for which IMGS slot is being annotated (upload id or result uuid). */
export type ImgsVisualPromptReplaceTarget =
	| { kind: 'upload'; uploadId: string }
	| { kind: 'nodeRunResult'; nodeRunResultUuid: string };

/** Gallery row item for Selected Images dialog (visual prompt entry). */
export type SelectedImagesDialogVisualItem =
	| { type: 'upload'; data: RunnitUpload }
	| { type: 'result'; data: RunnitNodeRunResult };

/** The pre-annotation image a current image was derived from. */
export type ImgsOriginalEntry = {
	upload?: RunnitUpload
	nodeRunResult?: RunnitNodeRunResult
};

/**
 * Originals keyed by the identity of the image that currently occupies the slot.
 * Keyed rather than positional so reordering and deleting images cannot desync it.
 */
export type ImgsOriginalsMap = Record<string, ImgsOriginalEntry>;

export interface ImgsValueState {
	uploads: RunnitUpload[]
	nodeRunResults: RunnitNodeRunResult[]
	originalImages: ImgsOriginalsMap
}

type ImgsCombinedSlot =
	| { type: 'upload'; data: RunnitUpload }
	| { type: 'nodeRunResult'; data: RunnitNodeRunResult };

/** Prefixed so an upload id can never collide with a node run result uuid. */
export function imgsImageKeyForTarget (target: ImgsVisualPromptReplaceTarget): string {
	return target.kind === 'upload'
		? `u:${target.uploadId}`
		: `r:${target.nodeRunResultUuid}`;
}

function imgsImageKeyForSlot (slot: ImgsCombinedSlot): string | null {
	if (slot.type === 'upload') {
		return slot.data?.id ? `u:${slot.data.id}` : null;
	}
	return slot.data?.uuid ? `r:${slot.data.uuid}` : null;
}

function combinedSlots (uploads: RunnitUpload[], nodeRunResults: RunnitNodeRunResult[]): ImgsCombinedSlot[] {
	return [
		...uploads.map((u) => ({ type: 'upload' as const, data: u })),
		...nodeRunResults.map((r) => ({ type: 'nodeRunResult' as const, data: r })),
	];
}

/** Slot index in combined order [...uploads, ...nodeRunResults]. */
export function resolveImgsSlotIndex (
	uploads: RunnitUpload[],
	nodeRunResults: RunnitNodeRunResult[],
	target: ImgsVisualPromptReplaceTarget,
): number {
	if (target.kind === 'upload') {
		return uploads.findIndex((u) => u && u.id === target.uploadId);
	}
	const i = nodeRunResults.findIndex((r) => r && r.uuid === target.nodeRunResultUuid);
	return i >= 0 ? uploads.length + i : -1;
}

function originalEntryFromCombinedSlot (
	currentImageAtIndex: ImgsCombinedSlot | null | undefined,
): ImgsOriginalEntry | null {
	if (!currentImageAtIndex) return null;
	return currentImageAtIndex.type === 'nodeRunResult'
		? { nodeRunResult: currentImageAtIndex.data }
		: { upload: currentImageAtIndex.data };
}

export function replaceTargetFromDialogItem (item: SelectedImagesDialogVisualItem | { type: string; data: unknown }): ImgsVisualPromptReplaceTarget | null {
	if (item.type === 'upload' && item.data && typeof item.data === 'object' && 'id' in item.data && (item.data as RunnitUpload).id) {
		return { kind: 'upload', uploadId: (item.data as RunnitUpload).id };
	}
	if (item.type === 'result' && item.data && typeof item.data === 'object' && 'uuid' in item.data && (item.data as RunnitNodeRunResult).uuid) {
		return { kind: 'nodeRunResult', nodeRunResultUuid: (item.data as RunnitNodeRunResult).uuid };
	}
	return null;
}

/**
 * Originals map for the current images, preserving each image's existing original and
 * seeding un-annotated images with themselves. Entries whose image is gone are dropped,
 * so deleting an image cleans up after itself.
 */
export function buildOriginalImagesMap (
	uploads: RunnitUpload[],
	nodeRunResults: RunnitNodeRunResult[],
	existing: ImgsOriginalsMap | null | undefined = {},
): ImgsOriginalsMap {
	const prev = existing || {};
	const next: ImgsOriginalsMap = {};
	combinedSlots(uploads, nodeRunResults).forEach((slot) => {
		const key = imgsImageKeyForSlot(slot);
		if (!key) return;
		const carried = prev[key];
		if (carried && (carried.upload || carried.nodeRunResult)) {
			next[key] = carried;
			return;
		}
		const self = originalEntryFromCombinedSlot(slot);
		if (self) next[key] = self;
	});
	return next;
}

/**
 * Whether the dialog item is an annotation composite — i.e. its stored original is a
 * different image than the one currently in the slot. Un-annotated images are seeded as
 * their own original, so those return false.
 */
export function isImgAnnotated (
	originalImages: ImgsOriginalsMap | null | undefined,
	item: { type: string; data: any },
): boolean {
	const target = replaceTargetFromDialogItem(item);
	if (!target) return false;
	const original = (originalImages || {})[imgsImageKeyForTarget(target)];
	if (!original) return false;
	if (target.kind === 'upload') {
		return !(original.upload && original.upload.id === target.uploadId);
	}
	return !(original.nodeRunResult && original.nodeRunResult.uuid === target.nodeRunResultUuid);
}

/** The original for whichever image the target currently points at. */
export function originalEntryForTarget (
	value: { originalImages?: ImgsOriginalsMap } | null | undefined,
	target: ImgsVisualPromptReplaceTarget | null | undefined,
): ImgsOriginalEntry | null {
	if (!value || !target) return null;
	const entry = (value.originalImages || {})[imgsImageKeyForTarget(target)];
	if (!entry || (!entry.upload && !entry.nodeRunResult)) return null;
	return entry;
}

/**
 * Replace combined slot `index` with the given upload/result and split back into uploads + nodeRunResults arrays.
 */
export function applyImgsResetAtCombinedSlot (
	uploads: RunnitUpload[],
	nodeRunResults: RunnitNodeRunResult[],
	index: number,
	original: ImgsOriginalEntry,
): { newUploads: RunnitUpload[]; newNodeRunResults: RunnitNodeRunResult[] } | null {
	const allImages = combinedSlots(uploads, nodeRunResults);
	if (index < 0 || index >= allImages.length) return null;
	if (original.upload) {
		allImages[index] = { type: 'upload', data: original.upload };
	} else if (original.nodeRunResult) {
		allImages[index] = { type: 'nodeRunResult', data: original.nodeRunResult };
	} else {
		return null;
	}
	return splitCombined(allImages);
}

function splitCombined (allImages: ImgsCombinedSlot[]) {
	const newUploads = allImages
		.filter((img): img is { type: 'upload'; data: RunnitUpload } => img.type === 'upload')
		.map((img) => img.data);
	const newNodeRunResults = allImages
		.filter((img): img is { type: 'nodeRunResult'; data: RunnitNodeRunResult } => img.type === 'nodeRunResult')
		.map((img) => img.data);
	return { newUploads, newNodeRunResults };
}

export function resetImgsValueAtTarget (
	value: { uploads?: RunnitUpload[]; nodeRunResults?: RunnitNodeRunResult[]; originalImages?: ImgsOriginalsMap } | null | undefined,
	target: ImgsVisualPromptReplaceTarget,
): (ImgsValueState & { index: number; original: ImgsOriginalEntry }) | null {
	if (!value) return null;
	const uploads = [...(value.uploads || [])];
	const nodeRunResults = [...(value.nodeRunResults || [])];
	const index = resolveImgsSlotIndex(uploads, nodeRunResults, target);
	if (index < 0) return null;
	const original = originalEntryForTarget(value, target);
	if (!original) return null;
	const split = applyImgsResetAtCombinedSlot(uploads, nodeRunResults, index, original);
	if (!split) return null;

	// The slot now holds the original image again; drop the annotated image's entry
	// so the restored image is its own original.
	const carried = { ...(value.originalImages || {}) };
	delete carried[imgsImageKeyForTarget(target)];

	return {
		uploads: split.newUploads,
		nodeRunResults: split.newNodeRunResults,
		originalImages: buildOriginalImagesMap(split.newUploads, split.newNodeRunResults, carried),
		index,
		original,
	};
}

export function replaceImgsValueAtTarget (
	value: { uploads?: RunnitUpload[]; nodeRunResults?: RunnitNodeRunResult[]; originalImages?: ImgsOriginalsMap } | null | undefined,
	target: ImgsVisualPromptReplaceTarget,
	upload: RunnitUpload,
): (ImgsValueState & { index: number }) | null {
	if (!value) return null;
	const uploads = [...(value.uploads || [])];
	const nodeRunResults = [...(value.nodeRunResults || [])];
	const index = resolveImgsSlotIndex(uploads, nodeRunResults, target);
	if (index < 0) return null;
	const existing = value.originalImages || {};

	const allImages = combinedSlots(uploads, nodeRunResults);
	// Carry the image's existing original forward, so annotating an already-annotated
	// image (A -> B -> C) still resets all the way back to A rather than to B.
	const carriedOriginal = originalEntryForTarget(value, target) ||
		originalEntryFromCombinedSlot(allImages[index]);

	allImages[index] = { type: 'upload', data: upload };
	const { newUploads, newNodeRunResults } = splitCombined(allImages);

	const carried = { ...existing };
	delete carried[imgsImageKeyForTarget(target)];
	const newOriginalImages = buildOriginalImagesMap(newUploads, newNodeRunResults, carried);
	if (carriedOriginal && upload?.id) {
		newOriginalImages[`u:${upload.id}`] = carriedOriginal;
	}

	return {
		uploads: newUploads,
		nodeRunResults: newNodeRunResults,
		originalImages: newOriginalImages,
		index,
	};
}

export function originalImageSourceFromEntry (
	original: ImgsOriginalEntry | null | undefined,
): { source: RunnitUpload | RunnitNodeRunResult; useCase: 'upload' | 'nodeRunResult' } | null {
	if (!original) return null;
	if (original.upload) {
		return { source: original.upload, useCase: 'upload' };
	}
	if (original.nodeRunResult) {
		return { source: original.nodeRunResult, useCase: 'nodeRunResult' };
	}
	return null;
}
